/**
 * Mingle Mage API client — owned sequence documents, immutable shares, and registry APIs.
 */
import { BaseApiClient } from '../client/base-client.js';
export const NO_AUTOMATIC_RETRY = {
    retries: 0,
    retryDelay: 0
};
export class MingleMageClient extends BaseApiClient {
    constructor(config) {
        super({
            ...config,
            // Last-resort default for direct `new MingleMageClient({})` that bypasses the factory
            // (mainly tests/local). Env-aware resolution lives in createMingleMageClient /
            // resolveMingleMageBaseURL (KORA-39); the client layer must not own env detection.
            baseURL: config.baseURL == null ? 'http://localhost:3004' : config.baseURL
        });
    }
    async listSequences() {
        return this.get('/api/v1/sequences');
    }
    async createSequence(request) {
        return this.post('/api/v1/sequences', request);
    }
    async getSequence(sequenceId) {
        return this.get(`/api/v1/sequences/${encodeURIComponent(sequenceId)}`);
    }
    async updateSequence(sequenceId, request) {
        return this.put(`/api/v1/sequences/${encodeURIComponent(sequenceId)}`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async deleteSequence(sequenceId, expectedRevision) {
        return this.delete(`/api/v1/sequences/${encodeURIComponent(sequenceId)}`, { expectedRevision }, undefined, NO_AUTOMATIC_RETRY);
    }
    /** MM-729 — the account's synced preferences; `document` is null before the first save. */
    async getPreferences() {
        return this.get('/api/v1/preferences');
    }
    /** MM-729 — merges the changed keys into the stored document. Not retried: a write. */
    async patchPreferences(request) {
        return this.patch('/api/v1/preferences', request, undefined, NO_AUTOMATIC_RETRY);
    }
    async listPropDefinitions() {
        return this.get('/api/v1/prop-definitions');
    }
    async createPropDefinition(request) {
        return this.post('/api/v1/prop-definitions', request);
    }
    async getPropDefinition(propDefinitionId) {
        return this.get(`/api/v1/prop-definitions/${encodeURIComponent(propDefinitionId)}`);
    }
    async updatePropDefinition(propDefinitionId, request) {
        return this.put(`/api/v1/prop-definitions/${encodeURIComponent(propDefinitionId)}`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async deletePropDefinition(propDefinitionId, expectedRevision) {
        return this.delete(`/api/v1/prop-definitions/${encodeURIComponent(propDefinitionId)}`, { expectedRevision }, undefined, NO_AUTOMATIC_RETRY);
    }
    async createStoredSequenceShare(sequenceId) {
        return this.post(`/api/v1/sequences/${encodeURIComponent(sequenceId)}/shares`);
    }
    async getSequenceShare(shareId) {
        return this.get(`/api/v1/sequence-shares/${encodeURIComponent(shareId)}`);
    }
    async listSequenceShares() {
        return this.get('/api/v1/sequence-shares');
    }
    async updateSequenceShare(shareId, update) {
        return this.patch(`/api/v1/sequence-shares/${encodeURIComponent(shareId)}`, update, undefined, NO_AUTOMATIC_RETRY);
    }
    async withdrawSequenceShare(shareId) {
        return this.post(`/api/v1/sequence-shares/${encodeURIComponent(shareId)}/withdrawal`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    /** MM-759 — hide an already-withdrawn share from the owner's list (the row is kept). No automatic retry. */
    async removeSequenceShare(shareId) {
        return this.post(`/api/v1/sequence-shares/${encodeURIComponent(shareId)}/removal`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    async createSequenceRelease(sequenceId, request) {
        return this.post(`/api/v1/sequences/${encodeURIComponent(sequenceId)}/releases`, request);
    }
    async listSequenceReleases(sequenceId) {
        return this.get(`/api/v1/sequences/${encodeURIComponent(sequenceId)}/releases`);
    }
    async getSequenceReleaseManagement(releaseId) {
        return this.get(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/management`);
    }
    async publishSequenceRelease(releaseId) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/publish`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    async withdrawSequenceRelease(releaseId) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/withdraw`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    async archiveSequenceRelease(releaseId) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/archive`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    async addSequenceReleaseAudienceGrant(releaseId, request) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/audience-grants`, request);
    }
    async revokeSequenceReleaseAudienceGrant(releaseId, grantId) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/audience-grants/${encodeURIComponent(grantId)}/revoke`, undefined, undefined, NO_AUTOMATIC_RETRY);
    }
    async getPublicSequenceRelease(releaseId) {
        return this.get(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}`);
    }
    /** MM-746 — the snapshot a reader may take into their library under the copy/remix policy. */
    async copySequenceRelease(releaseId, request) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/copies`, request);
    }
    async getReleaseDeliveryCapabilities() {
        return this.get('/api/v1/sequence-releases/delivery-capabilities');
    }
    async listAdminSequenceReleases() {
        return this.get('/api/v1/admin/sequence-releases');
    }
    async getAdminSequenceRelease(releaseId) {
        return this.get(`/api/v1/admin/sequence-releases/${encodeURIComponent(releaseId)}`);
    }
    async reportSequenceRelease(releaseId, request) {
        return this.post(`/api/v1/sequence-releases/${encodeURIComponent(releaseId)}/moderation-cases`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async appealModerationCase(caseId, statement) {
        return this.post(`/api/v1/moderation-cases/${encodeURIComponent(caseId)}/appeals`, { statement }, undefined, NO_AUTOMATIC_RETRY);
    }
    async listAdminModerationCases() {
        return this.get('/api/v1/admin/moderation-cases');
    }
    async getAdminModerationCase(caseId) {
        return this.get(`/api/v1/admin/moderation-cases/${encodeURIComponent(caseId)}`);
    }
    async transitionModerationCase(caseId, request) {
        return this.post(`/api/v1/admin/moderation-cases/${encodeURIComponent(caseId)}/transitions`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async getModerationEvidence(caseId) {
        return this.get(`/api/v1/admin/moderation-cases/${encodeURIComponent(caseId)}/evidence`);
    }
    async enforceModerationCase(caseId, request) {
        return this.post(`/api/v1/admin/moderation-cases/${encodeURIComponent(caseId)}/enforcement`, request, undefined, NO_AUTOMATIC_RETRY);
    }
    async getContentEnforcement(contentType, contentId) {
        return this.get(`/api/v1/admin/content-enforcement/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`);
    }
    // MM-616 — licensed procedure references. `contentId` is `<namespace>/<slug>`; each segment is
    // encoded on its own so the literal separating `/` survives into the two-segment route.
    async listLicensedProcedures() {
        return this.get('/api/v1/licensed-procedures');
    }
    async getLicensedProcedureBody(contentId, version) {
        const [namespace, slug] = contentId.split('/');
        return this.get(`/api/v1/licensed-procedures/${encodeURIComponent(namespace)}/${encodeURIComponent(slug)}/versions/${version}/body`);
    }
    // MM-727 — the factory catalog; 304 is whitelisted through `validateStatus` (a success here).
    // Kept deliberately small: this class ships in the Mingle Mage entry chunk, which has no headroom;
    // the admin methods live in `MingleMageFactoryAdminClient` so the app never bundles them.
    async getFactoryCatalog(ifNoneMatch) {
        const r = await this.request({
            url: '/api/v1/factory-catalog',
            headers: ifNoneMatch ? { 'If-None-Match': `"${ifNoneMatch}"` } : undefined,
            validateStatus: status => status < 300 || status === 304
        });
        return r.status === 304 ? { notModified: true } : { notModified: false, catalog: r.data };
    }
}
